<!--
 * @Author: chengleilei
 * @Date: 2021-10-19 16:18:34
 * @Description: 销售订单详情
-->
<template>
    <div class="special-order-detail" v-loading="pageLoading">
        <PageHeader title="销售订单详情" hidden-breadcrumb>
            <div slot="back" @click="jumpPrevPage">
                <Icon type="md-arrow-back" />
            </div>
        </PageHeader>
        <Card :bordered="false" dis-hover class="ivu-mt">
            <Tabs :value="currentTab" :animated="true">
                <TabPane label="订单信息" name="0">
                    <template>
                        <Button v-if="detailInfo.canRefund" type="primary" @click="onRefundBtnClick">退款</Button>
                        <Button
                            v-if="detailInfo.payType == 0 && detailInfo.afterSaleStatus != 5"
                            type="primary"
                            @click="showOffOrderModal = true"
                        >
                            取消订单
                        </Button>
                        <Card :bordered="false" title="" dis-hover class="ivu-mt-16 i-table-no-border">
                            <DescriptionList title="">
                                <Description term="订单状态：">
                                    <b>{{ getLabelByValue(saleOrderStatusDict, detailInfo.orderStatus) }} </b>
                                </Description>
                                <Description term="售后状态：">
                                    <b>{{ detailInfo.afterSaleStatus === 1 ? '-' : detailInfo.afterSaleStatusStr }}</b>
                                </Description>
                                <Description term="订单编号：">{{ detailInfo.orderNo || '-' }}</Description>
                                <Description term="下单时间：">{{ detailInfo.placeDate || '-' }}</Description>
                                <Description term="取消时间：" v-if="detailInfo.cancelTime">
                                    {{ detailInfo.cancelTime || '-' }}
                                </Description>
                                <Description term="关闭时间：" v-if="detailInfo.status == 3">
                                    {{ detailInfo.closeTime || '-' }}
                                </Description>
                                <Description term="订单类型：">
                                    {{ getLabelByValue(payChannelsDict, detailInfo.payType) }}
                                </Description>
                            </DescriptionList>
                        </Card>
                        <Card :bordered="false" title="支付信息" dis-hover class="i-table-no-border">
                            <DescriptionList title="">
                                <Description term="总金额：">
                                    {{ formatDetail(detailInfo.orderAmount, '元') }}
                                </Description>
                                <template v-if="detailInfo.status != 9">
                                    <Description term="返利抵用：">
                                        {{ formatDetail(detailInfo.deductionAmount, '元') }}
                                    </Description>
                                    <Description term="实收金额：">
                                        {{ formatDetail(detailInfo.netReceiptsAmount, '元') }}
                                    </Description>
                                    <Description term="支付手续费：">{{ detailInfo.payFee || '-' }}</Description>
                                    <Description term="支付时间：">{{ detailInfo.payTime || '-' }}</Description>
                                    <Description term="支付交易号：">{{ detailInfo.paySn || '-' }}</Description>
                                    <Description term="支付方式：">{{ detailInfo.payTypeStr || '-' }}</Description>
                                    <Description term="已退总金额：">
                                        {{ formatDetail(detailInfo.hasRefundAmount, '元') }}
                                    </Description>
                                </template>
                            </DescriptionList>
                        </Card>
                        <Card :bordered="false" title="门店信息" dis-hover class="i-table-no-border">
                            <DescriptionList title="">
                                <Description term="门店名称：">{{ detailInfo.storeName || '-' }}</Description>
                                <Description term="门店备注：">{{ detailInfo.remark || '-' }}</Description>
                                <Description term="联系人：">{{ detailInfo.ownerName || '-' }}</Description>
                                <Description term="联系电话：">{{ detailInfo.ownerPhone || '-' }}</Description>
                                <Description term="经销商：">{{ detailInfo.distributorName || '-' }}</Description>
                                <Description term="门店维护人：">{{ detailInfo.salesmanName || '-' }}</Description>
                            </DescriptionList>
                        </Card>
                        <Card :bordered="false" title="商品信息" dis-hover class="i-table-no-border">
                            <Table
                                :columns="productColumns"
                                :data="productList"
                                :no-data-text="'暂无商品信息'"
                                :no-filtered-data-text="'暂无商品信息'"
                            ></Table>
                        </Card>
                    </template>
                </TabPane>
                <TabPane label="退款单" name="1" v-if="payType == 'onLine'">
                    <TablePage :hasPage="false" :list="refundList" />
                </TabPane>
            </Tabs>
        </Card>
        <RefundModal :modal.sync="showRefundModal" :item="{ ...detailInfo, id }" @confirmOk="confirmOk" />
        <OffOrderModal :modal.sync="showOffOrderModal" :item="{ ...detailInfo, id }" @confirmOk="offOrderConfirmOk" />
        <RefundRecordModal ref="refundRecordModalRef" />
        <FooterToolbar>
            <Button class="ml" @click="jumpPrevPage">返回</Button>
        </FooterToolbar>
    </div>
</template>

<script>
import { renderButton } from '@/libs/util';
import { specialOrderDetail, listRefundOrder } from '@/api/sales';
import RefundModal from './components/refundModal';
import OffOrderModal from './components/offOrderModal';
import RefundRecordModal from './components/RefundRecordModal';
import TablePage from '@/components/tablePage';
import { transPathToName } from '@/libs/util';
import { getLabelByValue, saleOrderStatusDict, payChannelsDict } from '@/dicts';
import { baseProductColumns, onLineColumns, refundColumns } from './const';

export default {
    components: { TablePage, RefundModal, OffOrderModal, RefundRecordModal },
    data() {
        return {
            getLabelByValue,
            saleOrderStatusDict,
            payChannelsDict,
            showOffOrderModal: false,
            showRefundModal: false, // 退款弹窗显隐
            currentTab: '0', // 当前tab 0订单信息 1退款单
            id: this.$route.query.id || '',
            payType: this.$route.query.payType || '',
            pageLoading: false,
            detailInfo: {}, // 订单详情数据
            // 商品信息表头
            offLineProductColumns: [
                ...baseProductColumns.slice(0, 7),
                {
                    title: '已取消数量',
                    minWidth: 100,
                    render: (h, p) => {
                        return [this.detailInfo.afterSaleStatus === 1 ? 0 : p.row.hasCancelNum || '-'];
                    },
                },
                ...baseProductColumns.slice(7),
            ],
            onLineProductColumns: onLineColumns,
            productList: [], // 商品信息表格数据
            // 退款单列表
            refundList: {
                current: 1,
                size: window.$pageSize,
                total: 0,
                columns: [
                    ...refundColumns,
                    {
                        title: '详情',
                        key: 'oprate',
                        width: 90,
                        fixed: 'right',
                        render: (h, p) => {
                            return [
                                p.row.returnType === 1
                                    ? renderButton(h, { text: '查看', type: 'primary' }, () => {
                                          this.checkRecord(p.row);
                                      })
                                    : '-',
                            ];
                        },
                    },
                ],
                data: [],
            },
        };
    },
    computed: {
        productColumns() {
            return this.payType === 'onLine' ? this.onLineProductColumns : this.offLineProductColumns;
        },
    },
    async created() {
        this.pageLoading = true;
        try {
            await this.getData();
            await this.getRefundList();
        } finally {
            this.pageLoading = false;
        }
    },
    methods: {
        jumpPrevPage() {
            console.log(location.pathname);
            window.$basePageStore.closeTab(transPathToName(location.pathname));
        },
        // 处理详情信息展示
        formatDetail(val, suffix = '') {
            return val === null || val === undefined || val === '' ? '-' : `${val}${suffix}`;
        },
        // 退款单列表查看详情
        checkRecord(row) {
            this.$refs['refundRecordModalRef'].open(row);
        },
        // 确认取消订单刷新页面
        offOrderConfirmOk() {
            this.jumpPrevPage();
        },
        // 查询退款单列表
        async getRefundList() {
            const res = await listRefundOrder(this.id);
            this.refundList.data = res || [];
        },
        // 确认退款刷新页面
        confirmOk() {
            this.getData();
            this.getRefundList();
        },
        // 退款按钮单击事件
        onRefundBtnClick() {
            this.showRefundModal = true;
        },
        // 查特价订单详情
        async getData() {
            const params = { orderId: this.id };
            const res = await specialOrderDetail(params);
            if (res) {
                this.detailInfo = res || {};
                this.productList = res.itemsvo || [];
            }
        },
    },
};
</script>

<style lang="less" scoped>
.special-order-detail {
    padding-bottom: 50px;
    /deep/ .i-table-no-border .ivu-table th {
        background-color: #f8f8f9;
    }
}
</style>
